import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import SacredEntry from "./pages/SacredEntry";
import Contemplation from "./pages/Contemplation";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import ProfilingQuiz from "./pages/ProfilingQuiz";
import ClientDashboard from "./pages/ClientDashboard";
import CoachAssessmentMatrix from "./pages/CoachAssessmentMatrix";
import CoachDashboard from "./pages/CoachDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ExpertDashboard from "./pages/ExpertDashboard";
import Premium from "./pages/Premium";
import PricingPage from "./pages/PricingPage";
import TermsOfService from "./pages/TermsOfService";
import Profile from "./pages/Profile";
import MaintenanceOverlay from "./components/MaintenanceOverlay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// API Helper with Auth
export const apiCall = async (method, endpoint, data = null, token = null) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  
  try {
    const config = { method, url: `${API}${endpoint}`, headers };
    if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
      config.data = data;
    }
    const response = await axios(config);
    return { success: true, data: response.data };
  } catch (error) {
    const message = error.response?.data?.detail || error.message || "An error occurred";
    return { success: false, error: message };
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], requireSubscription = false, requireProfile = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Role-based redirect for dashboard route
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "coach") return <Navigate to="/coach" replace />;
    if (user.role === "expert") return <Navigate to="/expert" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  // Subscription gate for clients
  if (requireSubscription && user.role === "client") {
    const access = user.module_access || [];
    if (access.length === 0) {
      return <Navigate to="/pricing" replace />;
    }
  }

  // Profile completion gate for clients
  if (requireProfile && user.role === "client" && !user.profile_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("siwon_token"));
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        const result = await apiCall("GET", "/auth/me", null, token);
        if (result.success) {
          setUser(result.data);
        } else {
          localStorage.removeItem("siwon_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);
  
  const login = async (email, password) => {
    const result = await apiCall("POST", "/auth/login", { email, password });
    if (result.success) {
      const { token: newToken, user: userData } = result.data;
      localStorage.setItem("siwon_token", newToken);
      setToken(newToken);
      setUser(userData);
      toast.success("Welcome back to your sacred journey");
      return { success: true, user: userData };
    }
    toast.error(result.error);
    return { success: false, error: result.error };
  };
  
  const register = async (name, email, mobile, password) => {
    const result = await apiCall("POST", "/auth/register", { name, email, mobile, password });
    if (result.success) {
      const { token: newToken, user: userData } = result.data;
      localStorage.setItem("siwon_token", newToken);
      setToken(newToken);
      setUser(userData);
      toast.success("Your sacred journey begins");
      return { success: true };
    }
    toast.error(result.error);
    return { success: false, error: result.error };
  };
  
  const logout = () => {
    localStorage.removeItem("siwon_token");
    setToken(null);
    setUser(null);
    toast.info("Until we meet again on your path");
  };

  const loginWithToken = (newToken, userData) => {
    localStorage.setItem("siwon_token", newToken);
    setToken(newToken);
    setUser(userData);
  };
  
  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };
  
  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithToken, register, logout, updateUser }}>
      <MaintenanceOverlay />
      {children}
    </AuthContext.Provider>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-cosmic-void">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<SacredEntry />} />
            <Route path="/contemplation" element={<Contemplation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireSubscription>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profiling"
              element={
                <ProtectedRoute requireSubscription>
                  <ProfilingQuiz />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireSubscription requireProfile>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-assessment-matrix"
              element={
                <ProtectedRoute requireSubscription requireProfile>
                  <CoachAssessmentMatrix />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach"
              element={
                <ProtectedRoute allowedRoles={["coach", "admin"]}>
                  <CoachDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expert"
              element={
                <ProtectedRoute allowedRoles={["expert", "admin"]}>
                  <ExpertDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premium"
              element={
                <ProtectedRoute>
                  <Premium />
                </ProtectedRoute>
              }
            />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
